<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import { Moon, Sun, Monitor } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from "@/components/ui/tooltip";

const theme = ref("system");
const resolvedTheme = ref("dark");
const displayedIcon = ref("monitor");
const isTransitioning = ref(false);
const ripples = ref([]);
const rippleIdRef = ref(0);
const buttonRef = ref(null);

// Get system preference
const getSystemTheme = () => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

// Enable theme transition class
const enableTransition = () => {
  document.documentElement.classList.add("theme-transition");
};

// Disable theme transition class
const disableTransition = () => {
  document.documentElement.classList.remove("theme-transition");
};

// Apply theme to document
const applyTheme = (isDark, withTransition = false) => {
  if (withTransition) {
    enableTransition();
  }

  if (isDark) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
  resolvedTheme.value = isDark ? "dark" : "light";

  if (withTransition) {
    setTimeout(disableTransition, 350);
  }
};

// Get icon key based on theme state
const getIconKey = (t, resolved) => {
  if (t === "system") return "monitor";
  return resolved === "dark" ? "sun" : "moon";
};

// Create ripple effect
const createRipple = (event) => {
  const button = event.currentTarget;
  if (!button) return;

  const rect = button.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  const newRipple = {
    id: rippleIdRef.value++,
    x,
    y,
  };

  ripples.value.push(newRipple);

  // Remove ripple after animation
  setTimeout(() => {
    ripples.value = ripples.value.filter((r) => r.id !== newRipple.id);
  }, 600);
};

// Initialize theme on mount
onMounted(() => {
  const savedTheme = localStorage.getItem("theme");
  
  if (savedTheme === "light" || savedTheme === "dark") {
    theme.value = savedTheme;
    applyTheme(savedTheme === "dark", false);
    displayedIcon.value = getIconKey(savedTheme, savedTheme === "dark" ? "dark" : "light");
  } else if (savedTheme === "system") {
    theme.value = "system";
    const isDark = getSystemTheme() === "dark";
    applyTheme(isDark, false);
    displayedIcon.value = getIconKey("system", isDark ? "dark" : "light");
  } else {
    // First visit: default to dark mode
    theme.value = "dark";
    localStorage.setItem("theme", "dark");
    applyTheme(true, false);
    displayedIcon.value = getIconKey("dark", "dark");
  }

  // Listen for system theme changes
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleChange = (e) => {
    if (theme.value === "system") {
        applyTheme(e.matches, true);
    }
  };
  mediaQuery.addEventListener("change", handleChange);
  
  // Cleanup not strictly necessary for top-level component but good practice
  // unmounting logic could go here if needed
});

// Cycle through themes: system -> light -> dark -> system
const cycleTheme = (event) => {
  if (isTransitioning.value) return;

  // Create ripple effect
  createRipple(event);

  let newTheme;
  let newResolved;
  
  if (theme.value === "system") {
    newTheme = "light";
    newResolved = "light";
  } else if (theme.value === "light") {
    newTheme = "dark";
    newResolved = "dark";
  } else {
    newTheme = "system";
    newResolved = getSystemTheme();
  }

  // Start transition - fade out current icon
  isTransitioning.value = true;

  // After fade out, change icon and fade in
  setTimeout(() => {
    displayedIcon.value = getIconKey(newTheme, newResolved);
    theme.value = newTheme;
    
    if (newTheme === "system") {
      localStorage.setItem("theme", "system");
      applyTheme(getSystemTheme() === "dark", true);
    } else {
      localStorage.setItem("theme", newTheme);
      applyTheme(newTheme === "dark", true);
    }
  }, 150);

  // End transition
  setTimeout(() => {
    isTransitioning.value = false;
  }, 300);
};

const getTooltipText = () => {
  switch (theme.value) {
    case "system":
      return `Automático (${resolvedTheme.value === "dark" ? "oscuro" : "claro"})`;
    case "light":
      return "Modo claro";
    case "dark":
      return "Modo oscuro";
  }
};

const iconBaseClass = "h-4 w-4 transition-all duration-150 ease-in-out";
</script>

<template>
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <Button
          ref="buttonRef"
          variant="ghost"
          size="icon"
          @click="cycleTheme"
          class="h-9 w-9 overflow-hidden relative"
          :aria-label="getTooltipText()"
        >
          <!-- Ripple effects -->
          <span
            v-for="ripple in ripples"
            :key="ripple.id"
            class="absolute rounded-full bg-accent/30 animate-ripple pointer-events-none"
            :style="{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
              transform: 'translate(-50%, -50%)',
            }"
          ></span>
          
          <div class="relative flex items-center justify-center z-10">
            <div v-if="displayedIcon === 'monitor'" class="relative">
              <Monitor :class="[iconBaseClass, isTransitioning ? 'opacity-0 scale-50 rotate-90' : 'opacity-100 scale-100 rotate-0']" />
              <div 
                :class="[
                  'absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-accent border border-background transition-all duration-150',
                  isTransitioning ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
                ]" 
              />
            </div>
            <Sun v-if="displayedIcon === 'sun'" :class="[iconBaseClass, isTransitioning ? 'opacity-0 scale-50 rotate-90' : 'opacity-100 scale-100 rotate-0']" />
            <Moon v-if="displayedIcon === 'moon'" :class="[iconBaseClass, isTransitioning ? 'opacity-0 scale-50 rotate-90' : 'opacity-100 scale-100 rotate-0']" />
          </div>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{{ getTooltipText() }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
